import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Spin, message, Button, Table, Input, Form, Select, Space, Typography, Divider, Switch, Modal, Row, Col } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

import { SaveOutlined, PlusOutlined } from '@ant-design/icons';
import { theme } from '../../theme';

const { Title, Text } = Typography;
const { Option } = Select;

// Estilos para las filas anuladas
const styles = {
  rowAnulada: {
    backgroundColor: theme.colors.errorLight,
  }
};

// Mapeo de calidades a valores numéricos
const CALIDAD_TO_NUMERIC: { [key: string]: number } = {
  'C': 300,
  'C-1/4': 325,
  'C-1/2': 350,
  'C-3/4': 375,
  'D': 400,
  'D-1/4': 425,
  'D-1/2': 450,
  'D-3/4': 475,
  'E': 500,
  'E-1/4': 525,
  'E-1/2': 550,
  'E-3/4': 575
};

interface TrabajoClasificacionData {
  id: number;
  fecha: string;
  nro_planilla: number;
  alta: number;
  empresa: {
    razon: string;
    direccion: string;
    localidad: string;
    provincia: string;
  };
  detalles: {
    id: number;
    origen: string;
    cliente: string;
    lote: number;
    nroFardo: string;
    calidad: string;
    micronaire: number;
    longitud: number;
    resistencia: number;
    observaciones?: string;
    alta: number;
  }[];
}

interface ApiResponse {
  status: 'success' | 'error';
  message: string;
  detail?: string;
  data?: RelacionData;
}

interface RelacionItem {
  id: number;
  nc: string;
  denominacion: string;
  direccion: string;
  provincia: string;
  localidad: string;
  ndocumento: string;
  actividad: string;
  estado: string;
}

interface RelacionData {
  origenes: RelacionItem[];
  clientes: RelacionItem[];
}

interface RelacionResponse {
  origenes?: RelacionItem[];
  clientes?: RelacionItem[];
  [key: string]: any;
}

const TrabajoClasificacion: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { getAccessTokenSilently, user } = useAuth0();
  const [loading, setLoading] = useState(true);
  const [savingItems, setSavingItems] = useState<{ [key: number]: boolean }>({});
  const [data, setData] = useState<TrabajoClasificacionData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const id_empresa = user?.id_empresa;
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalNuevaMuestraVisible, setIsModalNuevaMuestraVisible] = useState(false);
  const [formNuevaMuestra] = Form.useForm();
  const [relacionData, setRelacionData] = useState<RelacionData>({ origenes: [], clientes: [] });
  const [loadingRelacion, setLoadingRelacion] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);



  const fetchData = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();

      const response = await axios.get<TrabajoClasificacionData>(
        `${process.env.REACT_APP_CLASIFICACION_URL}/emision_clasificacion/${id_empresa}/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!response.data || !response.data.detalles) {
        throw new Error('Los datos recibidos no tienen el formato esperado');
      }

      setData(response.data);

      // Inicializar el formulario con los datos recibidos
      const formValues = {
        detalles: response.data.detalles.map(detalle => ({
          id: detalle.id,
          lote: detalle.lote,
          calidad: detalle.calidad,
          micronaire: detalle.micronaire,
          longitud: detalle.longitud,
          resistencia: detalle.resistencia,
          observaciones: detalle.observaciones || '',
          alta: detalle.alta
        }))
      };

      form.setFieldsValue(formValues);
    } catch (error: any) {
      console.error('Error al obtener datos de clasificación:', error);
      const errorMessage = error.response?.data?.message || 'Error al cargar los datos de clasificación';
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelacionData = async () => {
    try {
      setLoadingRelacion(true);
      const token = await getAccessTokenSilently();

      console.log('Fetching relacion data...');
      const response = await axios.get<RelacionResponse>(
        `${process.env.REACT_APP_PADRON_URL}/leer_padron/${id_empresa}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      console.log('ESTRUCTURA COMPLETA DE LA RESPUESTA:', JSON.stringify(response.data, null, 2));

      let origenes: RelacionItem[] = [];
      let clientes: RelacionItem[] = [];

      // Intentar diferentes estructuras posibles
      if (Array.isArray(response.data)) {
        // Si la respuesta es directamente un array
        origenes = response.data;
        clientes = response.data;
      } else if (response.data && typeof response.data === 'object') {
        // Si es un objeto, intentar diferentes propiedades
        if (response.data.origenes && Array.isArray(response.data.origenes)) {
          origenes = response.data.origenes;
        }
        if (response.data.clientes && Array.isArray(response.data.clientes)) {
          clientes = response.data.clientes;
        }
      }

      console.log('Datos extraídos - Orígenes:', origenes);
      console.log('Datos extraídos - Clientes:', clientes);

      setRelacionData({
        origenes,
        clientes
      });

    } catch (error: any) {
      console.error('Error al obtener datos de relación:', error);
      console.error('Error details:', error.response?.data);
      message.error('Error al cargar los datos de origen y cliente');
      setRelacionData({ origenes: [], clientes: [] });
    } finally {
      setLoadingRelacion(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchData();
      fetchRelacionData();
    }
  }, [id, getAccessTokenSilently, form, id_empresa]);



  const handleEstadoChange = () => {
    setIsModalVisible(true);
  };

  const handleConfirmDesactivar = async () => {
    try {
      const token = await getAccessTokenSilently();
      await axios.put(
        `${process.env.REACT_APP_CLASIFICACION_URL}/baja_planilla/${id_empresa}/${id}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (data) {
        setData({ ...data, alta: 0 });
      }
      message.success('Planilla de clasificación desactivada correctamente');
      setIsModalVisible(false);
    } catch (error) {
      console.error('Error al desactivar la planilla:', error);
      message.error('Error al desactivar la planilla');
    }
  };

  const handleCancelDesactivar = () => {
    setIsModalVisible(false);
  };

  const handleGuardarDetalle = async (record: any, index: number) => {
    let detalleActualizado;
    try {
      setSavingItems(prev => ({ ...prev, [record.id]: true }));

      const values = await form.validateFields();
      const detalle = values.detalles[index];

      if (!detalle) {
        message.error('No se encontraron datos para guardar');
        return;
      }

      if (!record.id) {
        message.error('No se encontró el ID del detalle');
        return;
      }

      if (!detalle.calidad) {
        message.error('Debe seleccionar una calidad');
        return;
      }

      const token = await getAccessTokenSilently();

      // Asegurarnos de que el valor numérico sea un número entero
      const calidadNumerica = CALIDAD_TO_NUMERIC[detalle.calidad];
      if (typeof calidadNumerica !== 'number') {
        message.error('Error al determinar el valor numérico de la calidad');
        return;
      }

      // Primero consultar al control de clasificación
      const cliente_id = record.cliente_id || 0;
      const controlResponse = await axios.get(
        `${process.env.REACT_APP_CLASIFICACION_URL}/control_clasificaciones_edicion/${id_empresa}/${record.nroFardo}/${record.lote}/${detalle.lote}/${cliente_id}/${detalle.alta ? 1 : 0}/`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Si hay un mensaje de control, mostrarlo y esperar confirmación
      if (controlResponse.data && controlResponse.data.message) {
        return new Promise((resolve) => {
          let modalClosed = false;

          const modal = Modal.info({
            title: 'Control de Clasificación',
            content: (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <p>{controlResponse.data.message}</p>
              </div>
            ),
            okText: null,
            width: 600,
            centered: true,
            maskClosable: false,
            keyboard: false,
            onOk: () => {
              modalClosed = true;
              modal.destroy();
              resolve(false);
            }
          });

          // Prevenir el cierre automático
          const originalDestroy = modal.destroy;
          modal.destroy = () => {
            if (modalClosed) {
              originalDestroy.call(modal);
            }
          };
        });
      } else {
        // Si no hay mensaje, proceder directamente con el guardado
        await guardarDetalle(record, detalle, calidadNumerica, token);
      }
    } catch (error: any) {
      console.error('Error al guardar el detalle:', error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.detail ||
        'Error al guardar el detalle';
      message.error(errorMessage);

      // Mostrar detalles adicionales en la consola para debugging
      if (error.response?.data) {
        console.log('Detalles del error:', {
          status: error.response.status,
          data: error.response.data,
          detalleEnviado: detalleActualizado
        });
      }
    } finally {
      setSavingItems(prev => ({ ...prev, [record.id]: false }));
    }
  };

  // Función auxiliar para guardar el detalle
  const guardarDetalle = async (record: any, detalle: any, calidadNumerica: number, token: string) => {
    const detalleActualizado = {
      id: record.id,
      lote: parseInt(detalle.lote),
      calidad: detalle.calidad,
      calidad_numerica: calidadNumerica,
      micronaire: parseFloat(detalle.micronaire),
      longitud: parseFloat(detalle.longitud),
      resistencia: parseFloat(detalle.resistencia),
      observaciones: detalle.observaciones || '',
      alta: detalle.alta ? 1 : 0
    };

    console.log('Enviando datos al backend:', detalleActualizado);

    const response = await axios.post<ApiResponse>(
      `${process.env.REACT_APP_CLASIFICACION_URL}/cambios_clasificacion/${id_empresa}/${id}/${record.id}/`,
      detalleActualizado,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    if (response.data.status === 'success') {
      message.success('Detalle actualizado correctamente');
    } else {
      message.error(response.data.message || 'Error al guardar el detalle');
    }
  };

  const handleAgregarMuestra = async () => {
    try {
      setLoadingModal(true);
      console.log('Opening modal and fetching data...');
      await fetchRelacionData();
      setIsModalNuevaMuestraVisible(true);
    } catch (error) {
      console.error('Error al abrir el modal:', error);
      message.error('Error al cargar los datos');
    } finally {
      setLoadingModal(false);
    }
  };

  const handleCancelarNuevaMuestra = () => {
    setIsModalNuevaMuestraVisible(false);
    formNuevaMuestra.resetFields();
  };

  const handleSubmitNuevaMuestra = async () => {
    try {
      setLoadingSubmit(true);
      const values = await formNuevaMuestra.validateFields();
      const token = await getAccessTokenSilently();

      // Convertir '0' a 0 para origen y cliente
      const origen_id = values.origen === '0' ? 0 : values.origen;
      const cliente_id = values.cliente === '0' ? 0 : values.cliente;

      // Primero consultar al control de clasificación
      try {
        const controlResponse = await axios.get(
          `${process.env.REACT_APP_CLASIFICACION_URL}/control_clasificaciones_registro/${id_empresa}/${values.nroFardo}/${values.lote}/${cliente_id}/`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        // Si hay un mensaje de control, mostrarlo y esperar confirmación
        if (controlResponse.data && controlResponse.data.message) {
          return new Promise((resolve) => {
            let modalClosed = false;

            const modal = Modal.confirm({
              title: 'Control de Clasificación',
              content: (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <p>{controlResponse.data.message}</p>
                </div>
              ),
              okText: 'Continuar',
              cancelText: 'Cancelar',
              width: 600,
              centered: true,
              maskClosable: false,
              keyboard: false,
              onOk: async () => {
                modalClosed = true;
                modal.destroy();
                // Proceder con el guardado
                await guardarNuevaMuestra(values, origen_id, cliente_id, token);
                resolve(true);
              },
              onCancel: () => {
                modalClosed = true;
                modal.destroy();
                resolve(false);
              }
            });

            // Prevenir el cierre automático
            const originalDestroy = modal.destroy;
            modal.destroy = () => {
              if (modalClosed) {
                originalDestroy.call(modal);
              }
            };
          });
        } else {
          // Si no hay mensaje, proceder directamente con el guardado
          await guardarNuevaMuestra(values, origen_id, cliente_id, token);
        }
      } catch (error: any) {
        // Manejar el error específico del control
        const errorMessage = error.response?.data?.message || 'Error al verificar el control de clasificación';
        Modal.error({
          title: 'Error de Control',
          content: errorMessage,
          okText: 'Entendido',
          centered: true,
          width: 600
        });
        return;
      }
    } catch (error: any) {
      console.error('Error al agregar la muestra:', error);
      message.error('Error al agregar la muestra');
    } finally {
      setLoadingSubmit(false);
    }
  };

  // Función auxiliar para guardar la nueva muestra
  const guardarNuevaMuestra = async (values: any, origen_id: number, cliente_id: number, token: string) => {
    const nuevaMuestra = {
      ...values,
      origen_id,
      cliente_id,
      calidad_numerica: CALIDAD_TO_NUMERIC[values.calidad],
      alta: 1
    };

    const response = await axios.post<ApiResponse>(
      `${process.env.REACT_APP_CLASIFICACION_URL}/nueva_muestra/${id_empresa}/${id}/`,
      nuevaMuestra,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    if (response.data.status === 'success') {
      message.success('Muestra agregada correctamente');
      setIsModalNuevaMuestraVisible(false);
      formNuevaMuestra.resetFields();
      // Recargar los datos
      fetchData();
    } else {
      message.error(response.data.message || 'Error al agregar la muestra');
    }
  };

  const columns = [
    {
      title: 'N° Muestra',
      dataIndex: 'nroFardo',
      key: 'nroFardo',
      width: 100,
      align: 'center' as const,
    },
    {
      title: 'Origen',
      dataIndex: 'origen',
      key: 'origen',
      width: 200,
      render: (text: string, record: any, index: number) => (
        <Form.Item
          name={['detalles', index, 'origen']}
          style={{ margin: 0 }}
          initialValue={record.origen}
        >
          <Select
            showSearch
            optionFilterProp="label"
            loading={loadingRelacion}
            placeholder="Buscar origen..."
            filterOption={(input, option) => {
              const searchText = input.toLowerCase();
              const optionText = String(option?.label || '').toLowerCase();
              const optionValue = String(option?.value || '').toLowerCase();
              return optionText.includes(searchText) || optionValue.includes(searchText);
            }}
          >
            <Option value="" label="Sin origen">Sin origen</Option>
            {relacionData.origenes && relacionData.origenes.length > 0 ? (
              relacionData.origenes.map((origen) => (
                <Option
                  key={`origen-${origen.id}`}
                  value={origen.denominacion}
                  label={`${origen.nc} - ${origen.denominacion}`}
                >
                  {origen.nc} - {origen.denominacion}
                </Option>
              ))
            ) : (
              <Option value="" disabled>No hay orígenes disponibles</Option>
            )}
          </Select>
        </Form.Item>
      ),
    },
    {
      title: 'Cliente',
      dataIndex: 'cliente',
      key: 'cliente',
      width: 200,
      render: (text: string, record: any, index: number) => (
        <Form.Item
          name={['detalles', index, 'cliente']}
          style={{ margin: 0 }}
          initialValue={record.cliente}
        >
          <Select
            showSearch
            optionFilterProp="label"
            loading={loadingRelacion}
            placeholder="Buscar cliente..."
            filterOption={(input, option) => {
              const searchText = input.toLowerCase();
              const optionText = String(option?.label || '').toLowerCase();
              const optionValue = String(option?.value || '').toLowerCase();
              return optionText.includes(searchText) || optionValue.includes(searchText);
            }}
          >
            <Option value="" label="Sin cliente">Sin cliente</Option>
            {relacionData.clientes && relacionData.clientes.length > 0 ? (
              relacionData.clientes.map((cliente) => (
                <Option
                  key={`cliente-${cliente.id}`}
                  value={cliente.denominacion}
                  label={`${cliente.nc} - ${cliente.denominacion}`}
                >
                  {cliente.nc} - {cliente.denominacion}
                </Option>
              ))
            ) : (
              <Option value="" disabled>No hay clientes disponibles</Option>
            )}
          </Select>
        </Form.Item>
      ),
    },
    {
      title: 'Lote',
      dataIndex: 'id',
      key: 'lote',
      width: 80,
      align: 'center' as const,
      render: (id: number, record: any, index: number) => (
        <Form.Item
          name={['detalles', index, 'lote']}
          style={{ margin: 0 }}
          initialValue={record.lote}
        >
          <Input type="number" style={{ width: '100%', textAlign: 'center' }} />
        </Form.Item>
      ),
    },
    {
      title: 'Calidad',
      dataIndex: 'id',
      key: 'calidad',
      width: 120,
      render: (id: number, record: any, index: number) => (
        <Form.Item
          name={['detalles', index, 'calidad']}
          style={{ margin: 0 }}
        >
          <Select style={{ width: '100%' }}>
            <Option value="C">C</Option>
            <Option value="C-1/4">C-1/4</Option>
            <Option value="C-1/2">C-1/2</Option>
            <Option value="C-3/4">C-3/4</Option>
            <Option value="D">D</Option>
            <Option value="D-1/4">D-1/4</Option>
            <Option value="D-1/2">D-1/2</Option>
            <Option value="D-3/4">D-3/4</Option>
            <Option value="E">E</Option>
            <Option value="E-1/4">E-1/4</Option>
            <Option value="E-1/2">E-1/2</Option>
            <Option value="E-3/4">E-3/4</Option>
          </Select>
        </Form.Item>
      ),
    },
    {
      title: 'Micronaire',
      dataIndex: 'id',
      key: 'micronaire',
      width: 120,
      render: (id: number, record: any, index: number) => (
        <Form.Item
          name={['detalles', index, 'micronaire']}
          style={{ margin: 0 }}
        >
          <Input type="number" step="0.01" />
        </Form.Item>
      ),
    },
    {
      title: 'Longitud',
      dataIndex: 'id',
      key: 'longitud',
      width: 120,
      render: (id: number, record: any, index: number) => (
        <Form.Item
          name={['detalles', index, 'longitud']}
          style={{ margin: 0 }}
        >
          <Input type="number" step="0.01" />
        </Form.Item>
      ),
    },
    {
      title: 'Resistencia',
      dataIndex: 'id',
      key: 'resistencia',
      width: 120,
      render: (id: number, record: any, index: number) => (
        <Form.Item
          name={['detalles', index, 'resistencia']}
          style={{ margin: 0 }}
        >
          <Input type="number" step="0.01" />
        </Form.Item>
      ),
    },
    {
      title: 'Activo',
      dataIndex: 'id',
      key: 'alta',
      width: 100,
      align: 'center' as const,
      render: (id: number, record: any, index: number) => (
        <Form.Item
          name={['detalles', index, 'alta']}
          valuePropName="checked"
          style={{ margin: 0 }}
        >
          <Switch
            checkedChildren="Sí"
            unCheckedChildren="No"
            defaultChecked={record.alta === 1}
          />
        </Form.Item>
      ),
    },
    {
      title: 'Guardar',
      key: 'guardar',
      width: 80,
      align: 'center' as const,
      render: (_: unknown, record: any, index: number) => (
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={() => handleGuardarDetalle(record, index)}
          size="small"
          loading={savingItems[record.id] || false}
        />
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <div style={{ textAlign: 'center', color: 'red' }}>
          {error}
        </div>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <div style={{ textAlign: 'center' }}>
          No se encontraron datos para la clasificación
        </div>
      </Card>
    );
  }

  return (
    <div>
      <Card
        title={
          <div style={{ textAlign: 'center' }}>
            Trabajo de Clasificación - Planilla N° {data.nro_planilla}
          </div>
        }
        style={{ margin: '20px' }}
        headStyle={{
          backgroundColor: theme.colors.primaryDark,
          color: 'white',
          fontSize: '16px',
          padding: '12px'
        }}
      >
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
            <div>
              <p style={{ margin: '0 0 2px 0' }}><strong>Fecha:</strong> {new Date(data.fecha).toLocaleDateString()}</p>
              <p style={{ margin: '0 0 2px 0' }}><strong>Empresa:</strong> {data.empresa.razon}</p>
              <p style={{ margin: '0 0 2px 0' }}><strong>Dirección:</strong> {data.empresa.direccion}</p>
              <p style={{ margin: '0 0 2px 0' }}><strong>Localidad:</strong> {data.empresa.localidad}</p>
              <p style={{ margin: '0 0 2px 0' }}><strong>Provincia:</strong> {data.empresa.provincia}</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Button
                type="primary"
                icon={loadingModal ? <Spin size="small" /> : <PlusOutlined />}
                onClick={handleAgregarMuestra}
                style={{
                  backgroundColor: theme.colors.secondary,
                  borderColor: theme.colors.secondary,
                  color: 'white'
                }}
                title="Agregar una muestra"
                loading={loadingModal}
              />
              <span style={{
                color: data.alta === 1 ? theme.colors.success : theme.colors.error,
                fontWeight: 'bold',
                fontSize: '16px'
              }}>
                [{data.alta === 1 ? 'ACTIVO' : 'INACTIVO'}]
              </span>
              {data.alta === 1 && (
                <Button
                  type="primary"
                  danger
                  onClick={() => handleEstadoChange()}
                  style={{
                    backgroundColor: theme.colors.error,
                    borderColor: theme.colors.error,
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                >
                  DESACTIVAR
                </Button>
              )}
            </div>
          </div>
        </div>

        <Form form={form} layout="vertical">
          <Table
            scroll={{ x: 'max-content' }}
            columns={columns}
            dataSource={data.detalles}
            rowKey="id"
            pagination={false}
            size="middle"
            rowClassName={(record) => record.alta === 0 ? 'row-anulada' : ''}
            onRow={(record) => ({
              style: record.alta === 0 ? styles.rowAnulada : {}
            })}
          />
        </Form>
      </Card>

      <Modal
        title="Confirmar desactivación"
        open={isModalVisible}
        onOk={handleConfirmDesactivar}
        onCancel={handleCancelDesactivar}
        okText="Sí, desactivar"
        cancelText="Cancelar"
        okButtonProps={{ danger: true }}
      >
        <p>¿Está seguro de dar de baja la planilla de clasificación?</p>
      </Modal>

      <Modal
        title={
          <div style={{
            textAlign: 'center',
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            margin: '-20px -24px 20px -24px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: 'bold'
          }}>
            Agregar Nueva Muestra
          </div>
        }
        open={isModalNuevaMuestraVisible}
        onOk={handleSubmitNuevaMuestra}
        onCancel={handleCancelarNuevaMuestra}
        okText="Agregar"
        cancelText="Cancelar"
        width={800}
        okButtonProps={{ loading: loadingSubmit }}
      >
        <Form
          form={formNuevaMuestra}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="nroFardo"
                label="N° Muestra"
                rules={[{ required: true, message: 'Por favor ingrese el número de muestra' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="origen"
                label="Origen"
                rules={[{ required: true, message: 'Por favor seleccione el origen' }]}
              >
                <Select
                  loading={loadingRelacion}
                  showSearch
                  optionFilterProp="label"
                  placeholder="Buscar origen..."
                  filterOption={(input, option) => {
                    const searchText = input.toLowerCase();
                    const optionText = String(option?.label || '').toLowerCase();
                    const optionValue = String(option?.value || '').toLowerCase();
                    return optionText.includes(searchText) || optionValue.includes(searchText);
                  }}
                >
                  <Option value="0" label="Sin origen">Sin origen</Option>
                  {relacionData.origenes && relacionData.origenes.length > 0 ? (
                    relacionData.origenes.map((origen, index) => (
                      <Option
                        key={`origen-${index}`}
                        value={origen.id}
                        label={`${origen.nc} - ${origen.denominacion}`}
                      >
                        {origen.nc} - {origen.denominacion}
                      </Option>
                    ))
                  ) : (
                    <Option value="" disabled>No hay orígenes disponibles</Option>
                  )}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="cliente"
                label="Cliente"
                rules={[{ required: true, message: 'Por favor seleccione el cliente' }]}
              >
                <Select
                  loading={loadingRelacion}
                  showSearch
                  optionFilterProp="label"
                  placeholder="Buscar cliente..."
                  filterOption={(input, option) => {
                    const searchText = input.toLowerCase();
                    const optionText = String(option?.label || '').toLowerCase();
                    const optionValue = String(option?.value || '').toLowerCase();
                    return optionText.includes(searchText) || optionValue.includes(searchText);
                  }}
                >
                  <Option key="cliente-0" value="0" label={data?.empresa.razon || 'Empresa'}>
                    {data?.empresa.razon || 'Empresa'}
                  </Option>
                  {relacionData.clientes && relacionData.clientes.length > 0 ? (
                    relacionData.clientes.map((cliente, index) => (
                      <Option
                        key={`cliente-${index + 1}`}
                        value={cliente.id}
                        label={`${cliente.nc} - ${cliente.denominacion}`}
                      >
                        {cliente.nc} - {cliente.denominacion}
                      </Option>
                    ))
                  ) : (
                    <Option value="" disabled>No hay clientes disponibles</Option>
                  )}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lote"
                label="Lote"
                rules={[{ required: true, message: 'Por favor ingrese el lote' }]}
              >
                <Input type="number" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="calidad"
                label="Clasificación"
                rules={[{ required: true, message: 'Por favor seleccione la clasificación' }]}
              >
                <Select>
                  <Option value="C">C</Option>
                  <Option value="C-1/4">C-1/4</Option>
                  <Option value="C-1/2">C-1/2</Option>
                  <Option value="C-3/4">C-3/4</Option>
                  <Option value="D">D</Option>
                  <Option value="D-1/4">D-1/4</Option>
                  <Option value="D-1/2">D-1/2</Option>
                  <Option value="D-3/4">D-3/4</Option>
                  <Option value="E">E</Option>
                  <Option value="E-1/4">E-1/4</Option>
                  <Option value="E-1/2">E-1/2</Option>
                  <Option value="E-3/4">E-3/4</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="micronaire"
                label="Micronaire"
                rules={[{ required: true, message: 'Por favor ingrese el micronaire' }]}
              >
                <Input type="number" step="0.01" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="longitud"
                label="Longitud"
                rules={[{ required: true, message: 'Por favor ingrese la longitud' }]}
              >
                <Input type="number" step="0.01" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="resistencia"
                label="Resistencia"
                rules={[{ required: true, message: 'Por favor ingrese la resistencia' }]}
              >
                <Input type="number" step="0.01" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default TrabajoClasificacion; 